/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { createPortal } from 'react-dom'
import { Text, Pressable, Icon } from '@components/atoms'
import { motion } from 'framer-motion'

import { mono } from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	title: string
	show: boolean
	onToggleToast: (isShow: boolean) => void
}

/**
 * 화면 아래에서 올라와 3초 뒤 스스로 사라지는 한 줄 알림. /main · /admin 의 간단한 결과 통지는 이것으로 한다.
 *
 * `body` 포털 + `position: fixed` — 페이지를 스크롤해도 같은 자리에 있고, 전체 화면 오버레이(1250)·
 * MUI Dialog(1300) 위에 뜬다. 모달 안에서 부른 토스트가 모달 뒤로 깔리면 아무도 못 본다.
 */
export const Toast = (props: PropsType) => {
	// state
	const [show, setShow] = React.useState(false)

	const toastRemover = () => {
		setShow(false)

		if (typeof props.onToggleToast === 'function') {
			props.onToggleToast(false)
		}
	}

	React.useLayoutEffect(() => {
		setShow(props.show)
	}, [props.show])

	// 보이는 동안에만 센다. 사라지기 전에 새 문장이 오면 3초를 다시 센다.
	React.useEffect(() => {
		if (!show) {
			return undefined
		}

		const timeout = setTimeout(() => {
			toastRemover()
		}, 3000)

		return () => {
			clearTimeout(timeout)
		}
	}, [show, props.title])

	return createPortal(
		<motion.div
			css={style}
			initial={{ opacity: 0, bottom: '-5rem', y: '100%' }}
			animate={
				show
					? { opacity: 1, bottom: '3rem', y: 0 }
					: { opacity: 0, bottom: '-5rem', y: '100%' }
			}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.5, type: 'spring' }}
		>
			<Pressable className="toastButton" onClick={toastRemover}>
				<Text className="message">{props.title}</Text>
				<Icon name="close" className="closeIcon" />
			</Pressable>
		</motion.div>,
		document.body,
	)
}

const style = css({
	position: 'fixed',
	zIndex: 1400,
	width: 'fit-content',
	maxWidth: 'calc(100vw - 2rem)',
	height: 40,
	left: 0,
	right: 0,
	bottom: '3rem',
	margin: 'auto',

	'.toastButton': {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
		paddingLeft: '1rem',
		paddingRight: '1rem',
		backgroundColor: mono.ink,
		borderRadius: 2,

		'.message': {
			color: '#FFF',
		},

		'.closeIcon': {
			marginLeft: '0.5rem',
			color: '#FFF',
		},
	},
})
